import { useEffect } from "react";
import "./MirrorShotAnimation.css";

/**
 * PROFESSIONAL MIRROR SHOT ANIMATION
 * A steel-type move that fires reflective shards at the opponent
 * 
 * Key Features:
 * - Metallic charging effect on the player
 * - Multiple reflective mirror shards that fly toward the opponent
 * - Light reflection effects from the shards
 * - Impact explosion with glass-like shattering
 * - Professional game-quality visual effects
 */
export default function MirrorShotAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    const container = playerEl.closest(".battle-container");
    const bg = container?.querySelector(".battle-bg");
    if (!playerEl || !opponentEl || !container) return;

    // 1) Metallic charging effect on the player
    playerEl.classList.add("mirror-shot-charge");
    setTimeout(() => playerEl.classList.remove("mirror-shot-charge"), 600);

    // 2) Compute positions
    const pRect = playerEl.getBoundingClientRect();
    const oRect = opponentEl.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    const startX = (pRect.left + pRect.width / 2) - cRect.left;
    const startY = (pRect.top + pRect.height / 2) - cRect.top;
    const endX = (oRect.left + oRect.width / 2) - cRect.left;
    const endY = (oRect.top + oRect.height / 2) - cRect.top;
    const dx = endX - startX, dy = endY - startY;

    // 3) Create mirror sphere at player position
    const mirrorSphere = document.createElement("div");
    mirrorSphere.className = "mirror-shot-sphere";
    mirrorSphere.style.left = `${startX}px`;
    mirrorSphere.style.top = `${startY}px`;
    container.appendChild(mirrorSphere);

    // 4) After charging, shatter into mirror shards
    setTimeout(() => {
      // Remove the sphere
      mirrorSphere.remove();
      
      // Create mirror shards
      const count = 8;
      const shards = [];
      for (let i = 0; i < count; i++) {
        const shard = document.createElement("div");
        shard.className = "mirror-shot-shard";
        shard.style.left = `${startX}px`;
        shard.style.top = `${startY}px`;
        
        // Add slight variation to trajectory
        const offsetX = (Math.random() - 0.5) * 40;
        const offsetY = (Math.random() - 0.5) * 40;
        shard.style.setProperty("--dx", `${dx + offsetX}px`);
        shard.style.setProperty("--dy", `${dy + offsetY}px`);
        
        // Add slight delay between shards
        shard.style.animationDelay = `${i * 0.06}s`;
        
        // Add rotation variation
        shard.style.setProperty("--rotation", `${Math.random() * 720 - 360}deg`);
        
        container.appendChild(shard);
        shards.push(shard);
        
        // Create light reflection effect
        const reflection = document.createElement("div");
        reflection.className = "mirror-shot-reflection";
        reflection.style.left = `${startX}px`;
        reflection.style.top = `${startY}px`;
        reflection.style.setProperty("--dx", `${dx + offsetX}px`);
        reflection.style.setProperty("--dy", `${dy + offsetY}px`);
        reflection.style.animationDelay = `${i * 0.06}s`;
        container.appendChild(reflection);
        shards.push(reflection);
      }

      // 5) After the last shard hits, do impact + cleanup
      const lastShard = shards[shards.length - 2]; // Get the last actual shard (not reflection)
      const handleImpact = () => {
        shards.forEach(s => s.remove());

        // Create impact explosion
        const impact = document.createElement("div");
        impact.className = "mirror-shot-impact";
        impact.style.left = `${endX}px`;
        impact.style.top = `${endY}px`;
        container.appendChild(impact);

        // Create glass shatter effect
        for (let i = 0; i < 12; i++) {
          const shatter = document.createElement("div");
          shatter.className = "mirror-shot-shatter";
          shatter.style.left = `${endX}px`;
          shatter.style.top = `${endY}px`;
          
          // Random angle for shattering
          const angle = Math.random() * Math.PI * 2;
          const distance = 10 + Math.random() * 40;
          shatter.style.setProperty("--angle", `${angle}rad`);
          shatter.style.setProperty("--distance", `${distance}px`);
          
          container.appendChild(shatter);
          
          // Auto-remove shatter pieces
          setTimeout(() => shatter.remove(), 600);
        }

        // Shake & tint the opponent
        opponentEl.classList.add("mirror-shot-hit");
        setTimeout(() => opponentEl.classList.remove("mirror-shot-hit"), 400);

        // Flash the background
        if (bg) {
          bg.classList.add("mirror-shot-bg");
          setTimeout(() => bg.classList.remove("mirror-shot-bg"), 500);
        }

        // Create ripple effect
        const ripple = document.createElement("div");
        ripple.className = "mirror-shot-ripple";
        ripple.style.left = `${endX}px`;
        ripple.style.top = `${endY}px`;
        container.appendChild(ripple);
        
        setTimeout(() => {
          impact.remove();
          ripple.remove();
          onComplete();
        }, 600);

        lastShard.removeEventListener("animationend", handleImpact);
      };
      
      lastShard.addEventListener("animationend", handleImpact);
    }, 600); // After charging effect

    // Cleanup if unmounted early
    return () => {
      playerEl.classList.remove("mirror-shot-charge");
      opponentEl.classList.remove("mirror-shot-hit");
      if (bg) bg.classList.remove("mirror-shot-bg");
      
      // Remove any created elements
      const elements = container.querySelectorAll(".mirror-shot-sphere, .mirror-shot-shard, .mirror-shot-reflection, .mirror-shot-impact, .mirror-shot-ripple, .mirror-shot-shatter");
      elements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set the move type for proper coloring in the battle UI
MirrorShotAnimation.moveType = "steel";
